// Вкладки реестра людей по состоянию: как называются, как считаются и что сказать, когда
// вкладка пуста. Чистые функции без React: их гоняют тесты, а экран только рисует.
//
// ## Откуда числа
//
// Реестр приезжает ОДНИМ ответом без отбора по состоянию (`queryFromRoute`), и вкладки делят его
// на клиенте. Поэтому переключение мгновенное, а число на вкладке и строки под ней берутся из
// одного источника и разойтись не могут. Пять запросов «дай счётчик по состоянию» дали бы те же
// числа и пять обращений к серверу.
//
// Вкладки честны, только пока приехали ВСЕ люди выборки. Страница неполная (строк меньше, чем
// `total`) — число на вкладке было бы числом на куске, выданным за итог. Тогда вкладок нет, а
// работает прежняя подсказка «Показаны первые N из M».
//
// ## Название вкладки короче названия состояния
//
// Состояние в строке списка и в контракте называется «Межвахтовый отдых», а вкладка — «Межвахта».
// Это осознанная уступка ширине: в колонке списка 416 точек, и пять вкладок с числами в неё
// помещаются впритык. Полное название при этом остаётся там, где места хватает.

import type { EmployeeStatus } from '@/shared/api/endpoints/people';
import { isEmployeeStatus } from '@/shared/api/endpoints/people';
import { countLabel } from '@/shared/text/plural';

import { PERSON_FORMS, PERSON_FORMS_GENITIVE } from './dictionaries';
import { STATUS_ALL, type RegistryTab } from './route';

/** Порядок вкладок смысловой: сначала те, кто работает, в конце те, кого уже нет. */
export const TAB_STATUS_ORDER: readonly EmployeeStatus[] = [
  'on_shift',
  'between_shifts',
  'reserve',
  'leave',
  'sick',
  'fired',
];

export const REGISTRY_TAB_TITLES: Readonly<Record<RegistryTab, string>> = {
  on_shift: 'На вахте',
  between_shifts: 'Межвахта',
  reserve: 'Резерв',
  leave: 'Отпуск',
  sick: 'Больничный',
  fired: 'Уволены',
  [STATUS_ALL]: 'Все',
};

/** Слово после числа над списком: «133 человека · на вахте». У «Все» слова нет: «315 человек». */
const CAPTION_TAILS: Readonly<Record<EmployeeStatus, string>> = {
  on_shift: 'на вахте',
  between_shifts: 'межвахта',
  reserve: 'резерв',
  leave: 'отпуск',
  sick: 'больничный',
  fired: 'уволены',
};

/** Подпись над списком по активной вкладке: «133 человека · на вахте», «315 человек». */
export function listCaption(tab: RegistryTab, count: number): string {
  const people = countLabel(count, PERSON_FORMS);
  return tab === STATUS_ALL ? people : `${people} · ${CAPTION_TAILS[tab]}`;
}

/** Сколько людей на каждой вкладке. `all` — вся выборка, `on_shift` и прочие — её части. */
export type TabCounts = Readonly<Record<RegistryTab, number>>;

export function countTabs(people: readonly { status: EmployeeStatus }[]): TabCounts {
  const counts: Record<RegistryTab, number> = {
    on_shift: 0,
    between_shifts: 0,
    reserve: 0,
    leave: 0,
    sick: 0,
    fired: 0,
    [STATUS_ALL]: people.length,
  };
  for (const person of people) {
    // Состояние, которого нет в контракте, в части не попадает — только в «Все». Придумывать
    // ему вкладку незачем: заморожен набор схемой базы, и новое состояние начинается с контракта.
    if (isEmployeeStatus(person.status)) {
      counts[person.status] += 1;
    }
  }
  return counts;
}

/**
 * Какие вкладки рисовать. «Отпуск» и «Больничный» только когда в них кто-то есть (на боевых
 * данных 01.10.2026 обе пусты, и две вкладки с нулями были бы шумом), либо когда одна из них
 * открыта по ссылке: вкладка, на которой человек стоит, обязана быть видна.
 */
export function visibleTabs(counts: TabCounts, active: RegistryTab): RegistryTab[] {
  const rare: readonly EmployeeStatus[] = ['leave', 'sick'];
  const statuses = TAB_STATUS_ORDER.filter(
    (status) => !rare.includes(status) || counts[status] > 0 || status === active,
  );
  return [...statuses, STATUS_ALL];
}

/** Люди выбранной вкладки. «Все» отдаёт выборку целиком. */
export function inTab<T extends { status: EmployeeStatus }>(
  people: readonly T[],
  tab: RegistryTab,
): T[] {
  return tab === STATUS_ALL ? [...people] : people.filter((person) => person.status === tab);
}

// ── Пустота ───────────────────────────────────────────────────────────────────────────────
// Правило 7 ПРАВИЛ: пустой список — это состояние с причиной и следующим действием, а не белое
// поле. Причин пять, и слипаться они не имеют права: по каждой человек делает своё. В каждой
// ровно одна фраза о том, что случилось, одна о том, где люди есть, и одна кнопка: один и тот же
// факт («нашлось 2») в заголовке и под ним читается как два разных, и между ними ищут разницу.
//
//   реестр пуст вовсе                     → «В реестре пока никого нет» (кнопка «Новый сотрудник»
//                                           стоит в шапке списка всегда, здесь на неё ссылка)
//   поиск ничего не нашёл нигде           → «По запросу «…» никого нет», «Очистить поиск»
//   отбор ничего не оставил нигде         → «Под этот отбор людей нет», «Снять отбор»
//   пуста вкладка, в других люди есть     → где они, и «Показать всех»
//   то же под поиском                     → где они, и «Искать среди всех»

export type EmptyAction = 'show-all' | 'search-all' | 'clear-narrowing';

export interface EmptyTexts {
  kind: 'registry' | 'narrowed' | 'tab';
  title: string;
  description: string;
  /** Кнопка под текстом; null — кнопки нет. */
  action: { kind: EmptyAction; label: string } | null;
}

/** Пустая вкладка без поиска и отборов: одной строкой, без лишних слов. */
const EMPTY_PLAIN: Readonly<Record<EmployeeStatus, string>> = {
  on_shift: 'На вахте сейчас никого нет',
  between_shifts: 'На межвахте сейчас никого нет',
  reserve: 'В резерве никого нет',
  leave: 'В отпуске никого нет',
  sick: 'На больничном никого нет',
  fired: 'Уволенных нет',
};

/** «Где нет»: начало фразы, когда к пустой вкладке добавляется поиск или отбор. */
const EMPTY_WHERE: Readonly<Record<EmployeeStatus, string>> = {
  on_shift: 'На вахте',
  between_shifts: 'На межвахте',
  reserve: 'В «Резерве»',
  leave: 'В отпуске',
  sick: 'На больничном',
  fired: 'Среди уволенных',
};

const REGISTRY_EMPTY: EmptyTexts = {
  kind: 'registry',
  title: 'В реестре пока никого нет',
  description:
    'Заведите первую карточку кнопкой «Новый сотрудник» вверху списка: дальше человека можно будет поставить на объект и назначить тариф.',
  action: null,
};

/**
 * Ни в одном состоянии никого нет, а выборка чем-то сужена.
 *
 * Поиск без отборов называется поиском («По запросу «Иванов» никого нет»), а не «отбором»: слово
 * «отбор» в продукте значит ящик «Отборы» под поиском, и человеку, который только набрал
 * фамилию, оно ничего не говорит. Шаг один, и он назван тем, что кнопка делает.
 */
function narrowedEmpty(query: string | undefined, otherNarrowing: boolean): EmptyTexts {
  if (query !== undefined && !otherNarrowing) {
    return {
      kind: 'narrowed',
      title: `По запросу «${query}» никого нет`,
      description: 'Проверьте написание: ищем по фамилии, имени, табельному номеру и телефону.',
      action: { kind: 'clear-narrowing', label: 'Очистить поиск' },
    };
  }
  return {
    kind: 'narrowed',
    title: 'Под этот отбор людей нет',
    description: 'Снимите отбор, и список вернётся.',
    action: { kind: 'clear-narrowing', label: 'Снять отбор' },
  };
}

/** «межвахта 96, резерв 84, уволены 2»: где люди есть, кроме пустой вкладки. */
function elsewhere(counts: TabCounts, tab: RegistryTab): string {
  return TAB_STATUS_ORDER.filter((status) => status !== tab && counts[status] > 0)
    .map((status) => `${CAPTION_TAILS[status]} ${counts[status]}`)
    .join(', ');
}

export interface EmptyInput {
  /** Какая вкладка открыта. */
  tab: RegistryTab;
  /** Сколько людей на каждой вкладке В ТОЙ ЖЕ выборке, что под ней (с поиском и отборами). */
  counts: TabCounts;
  /** Поиск, если он задан. */
  query: string | undefined;
  /** Сужена ли выборка чем-то, кроме поиска и состояния: объект, регион, разряд, долг, срез. */
  otherNarrowing: boolean;
}

/**
 * Что сказать, когда под вкладкой нет ни одного человека.
 *
 * `counts` посчитаны по той же выборке, что и список: «в других состояниях нашлось 2» относится
 * к тому же поиску и тем же отборам, а не ко всему реестру. Поэтому фраза «В реестре 315 человек»
 * говорится только без поиска и отборов, а под ними — «в других состояниях».
 */
export function describeEmpty({ tab, counts, query, otherNarrowing }: EmptyInput): EmptyTexts {
  const narrowed = query !== undefined || otherNarrowing;

  if (counts[STATUS_ALL] === 0) {
    return narrowed ? narrowedEmpty(query, otherNarrowing) : REGISTRY_EMPTY;
  }
  // «Все» пусто только вместе со всей выборкой; сюда дойти нельзя, но ответ на всякий случай
  // честный: человек увидит причину, а не пустоту.
  if (tab === STATUS_ALL) {
    return narrowedEmpty(query, otherNarrowing);
  }

  // Где люди есть — одна фраза, и в заголовок она не попадает: заголовок говорит только, чего
  // здесь нет. Раньше «нашлось 1» стояло и в заголовке, и под ним.
  const found = `В других состояниях нашлось ${counts[STATUS_ALL]}: ${elsewhere(counts, tab)}.`;
  if (query !== undefined) {
    const what = otherNarrowing ? 'по этому запросу и отбору' : `по запросу «${query}»`;
    return {
      kind: 'tab',
      title: `${EMPTY_WHERE[tab]} ${what} никого нет`,
      description: found,
      action: { kind: 'search-all', label: 'Искать среди всех' },
    };
  }
  if (otherNarrowing) {
    return {
      kind: 'tab',
      title: `${EMPTY_WHERE[tab]} под этот отбор никого нет`,
      description: found,
      action: { kind: 'show-all', label: 'Показать всех' },
    };
  }
  return {
    kind: 'tab',
    title: EMPTY_PLAIN[tab],
    description: `В реестре ${countLabel(counts[STATUS_ALL], PERSON_FORMS)}: ${elsewhere(counts, tab)}`,
    action: { kind: 'show-all', label: 'Показать всех' },
  };
}

// ── Слова подтверждения после смены состояния ─────────────────────────────────────────────
// Человеку сменили состояние, а строка из открытой вкладки ушла: «Резерв» больше не видит того,
// кого только что перевели «На вахте». Без слов о том, куда он делся, это читается как потеря
// человека из списка. Поэтому подтверждение называет вкладку, где искать. Вкладка «Все» никого
// не теряет, и человек, оставшийся в той же вкладке, никуда не ушёл: тогда «куда» не говорится.

/**
 * Вторая строка подтверждения: «Теперь: вкладка «Резерв»». Пусто, когда человек остался там же.
 *
 * `open` — вкладка, которую человек видел, нажимая «Сменить статус»; `to` — состояние, в которое
 * перевели.
 */
export function changeTabNote(open: RegistryTab, to: EmployeeStatus): string | undefined {
  if (open === STATUS_ALL || open === to) {
    return undefined;
  }
  return `Теперь: вкладка «${REGISTRY_TAB_TITLES[to]}»`;
}

export interface ChangeToast {
  /** Главная строка подтверждения. */
  title: string;
  /** Подробность: куда ушли и кого не тронули; пусто, когда сказать нечего. */
  note: string | undefined;
}

/** Подтверждение смены состояния у ОДНОГО человека: «Состояние изменено» и куда он ушёл. */
export function stateChangeToast(open: RegistryTab, to: EmployeeStatus): ChangeToast {
  return { title: 'Состояние изменено', note: changeTabNote(open, to) };
}

/**
 * Подтверждение смены состояния ПАЧКОЙ.
 *
 * Все изменённые ушли в одно состояние, а значит в одну вкладку: куда именно, известно по коду,
 * и «кто куда ушёл, смотрите в числах вкладок» было бы менее точным. Тех, кого не тронули, тоже
 * называет подтверждение: панель выделения на успехе исчезает вместе с выделением, и рассказать
 * про них больше некому.
 *
 * `null` — говорить не о чем: никого не выбирали.
 */
export function bulkChangeToast(
  open: RegistryTab,
  to: EmployeeStatus,
  result: { updated: number; skipped: number },
): ChangeToast | null {
  if (result.updated === 0 && result.skipped === 0) {
    return null;
  }
  const notTouched =
    result.skipped === 0
      ? undefined
      : `Не тронуто: ${result.skipped}, они вне вашей области видимости`;

  if (result.updated === 0) {
    return { title: 'Состояние не изменилось ни у кого', note: notTouched };
  }
  const title =
    result.updated === 1
      ? 'Состояние изменено'
      : `Состояние изменено у ${countLabel(result.updated, PERSON_FORMS_GENITIVE)}`;
  const note = [changeTabNote(open, to), notTouched].filter(
    (часть): часть is string => часть !== undefined,
  );
  return { title, note: note.length === 0 ? undefined : note.join('. ') };
}
